/**
 * Card avatar milestones: each one reached once gives a level (any order). The checks live on the
 * server (server/lib/cardAvatars.ts); keys and texts are shared so Mi colección can list them.
 */
import ladders from "./avatarLadders.json";
import type { LadderFile, StarterLine } from "../packages/evolving-card-avatars/src/types";

export const MILESTONE_KEYS = [
  "account",
  "verified",
  "profile",
  "newsletter",
  "watch",
  "first_order",
  "transfer",
  "free_shipping",
  "presale",
  "orders_3",
  "big_order",
  "orders_5",
  "spent_500",
  "orders_10",
  "anniversary",
  "spent_1000",
  // Community market sellers.
  "first_sale",
  "sales_10",
  "five_stars",
] as const;
export type MilestoneKey = (typeof MILESTONE_KEYS)[number];

export const MILESTONE_TEXT: Record<MilestoneKey, { es: string; en: string }> = {
  account: { es: "Crear tu cuenta", en: "Create your account" },
  verified: { es: "Verificar tu email", en: "Verify your email" },
  profile: { es: "Completar tus datos de envío", en: "Fill in your shipping details" },
  newsletter: { es: "Suscribirte a las novedades", en: "Subscribe to our news" },
  watch: { es: "Activar tu primera alerta de precio o stock", en: "Set your first price or stock alert" },
  first_order: { es: "Tu primera compra", en: "Your first purchase" },
  transfer: { es: "Pagar con transferencia o cripto", en: "Pay by bank transfer or crypto" },
  free_shipping: { es: "Una compra con envío gratis", en: "A purchase with free shipping" },
  presale: { es: "Reservar una preventa", en: "Reserve a pre-sale" },
  orders_3: { es: "3 compras", en: "3 purchases" },
  big_order: { es: "Una compra de US$ 100 o más", en: "A purchase of US$100 or more" },
  orders_5: { es: "5 compras", en: "5 purchases" },
  spent_500: { es: "US$ 500 en compras", en: "US$500 in purchases" },
  orders_10: { es: "10 compras", en: "10 purchases" },
  anniversary: { es: "Un año desde tu primera compra", en: "One year since your first purchase" },
  spent_1000: { es: "US$ 1.000 en compras", en: "US$1,000 in purchases" },
  first_sale: { es: "Tu primera venta en el mercado", en: "Your first sale in the market" },
  sales_10: { es: "10 ventas en el mercado", en: "10 sales in the market" },
  five_stars: { es: "5 estrellas con 10 reseñas como vendedor", en: "5 stars with 10 reviews as a seller" },
};

export const LADDERS = ladders as LadderFile;
export const STARTER_LINES: StarterLine[] = LADDERS.lines;
export const MAX_LEVEL = LADDERS.levels;

export const findStarter = (key: string | null | undefined): StarterLine | null => STARTER_LINES.find((l) => l.key === key) ?? null;

/** Public paths of a card's images (served from public/avatar-cards). */
export const cardImage = (id: string) => `/avatar-cards/cards/${id}.webp`;
export const cardArt = (id: string) => `/avatar-cards/cards/${id}-art.webp`;

/** GET /api/me/collection */
export interface CollectionView {
  starter: string | null;
  level: number;
  maxLevel: number;
  /** Level whose card is the avatar (null when the avatar is a photo or no starter yet). */
  avatarLevel: number | null;
  mode: "card" | "photo";
  /** Highest level already celebrated: above it, the site shows the evolution animation. */
  seenLevel: number;
  hasPhoto: boolean;
  avatarUrl: string | null;
  milestones: { key: string; reachedAt: string | null }[];
  /** The starter's cards; locked ones (above `level`) only say their level. */
  cards: { level: number; id: string | null; name: string | null; rarity: string | null; image: string | null; art: string | null }[];
}
